import {
  createTheme,
  MantineColorsTuple,
  mergeThemeOverrides,
  DEFAULT_THEME,
  defaultVariantColorsResolver,
  VariantColorsResolver,
  parseThemeColor,
  rgba,
  darken
} from '@mantine/core';

/**
 * MentionedMe Brand Colors
 */
const brandPrimary: MantineColorsTuple = [
  '#f3e8ff', // 0 - lightest
  '#d9b8ff', // 1
  '#bf88ff', // 2
  '#a658ff', // 3
  '#8d28ff', // 4
  '#7423e0', // 5 - lighter for better contrast
  '#4D18C4', // 6 - main brand color (moved from 5 to 6 - Mantine uses index 6 for filled buttons)
  '#3d139d', // 7
  '#2e0e76', // 8
  '#1f094f'  // 9 - darkest
];

const brandSecondary: MantineColorsTuple = [
  '#f8f0ff', // 0 - lightest
  '#ead9ff', // 1
  '#dcc2ff', // 2
  '#ceabff', // 3
  '#B894F0', // 4 - main secondary color
  '#a37de6', // 5
  '#8e66cc', // 6
  '#794fb3', // 7
  '#643899', // 8
  '#4f2180'  // 9 - darkest
];

/**
 * Variant Color Resolver
 *
 * CRITICAL: This function is REQUIRED in Mantine 8 for custom colors to work with variants.
 * Without this, custom colors (brandPrimary, brandSecondary) are ignored and components
 * fall back to default colors.
 *
 * This resolver handles how our custom colors are applied to different button/ActionIcon variants:
 * - filled: Solid background with brand color, white text
 * - light: Semi-transparent background (10% opacity), brand color text
 * - outline: Transparent background, brand color border and text
 * - subtle: Transparent background, brand color text (no border)
 * - default: Light gray background, brand color text
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const defaultResolvedColors = defaultVariantColorsResolver(input);
  const parsedColor = parseThemeColor({
    color: input.color || input.theme.primaryColor,
    theme: input.theme,
  });

  // Handle our custom brand colors (brandPrimary and brandSecondary)
  if (parsedColor.isThemeColor && (parsedColor.color === 'brandPrimary' || parsedColor.color === 'brandSecondary')) {
    // Explicitly get the color value from theme - index 6 is our main brand color
    const colorValue = input.theme.colors[parsedColor.color]?.[6] || parsedColor.value;

    if (input.variant === 'filled') {
      return {
        background: colorValue,
        hover: darken(colorValue, 0.1),
        color: 'var(--mantine-color-white)',
        border: `1px solid ${colorValue}`,
      };
    }

    if (input.variant === 'light') {
      return {
        background: rgba(colorValue, 0.1),
        hover: rgba(colorValue, 0.15),
        color: colorValue,
        border: '1px solid transparent',
      };
    }

    if (input.variant === 'outline') {
      return {
        background: 'transparent',
        hover: rgba(colorValue, 0.05),
        color: colorValue,
        border: `1px solid ${colorValue}`,
      };
    }

    if (input.variant === 'subtle') {
      return {
        background: 'transparent',
        hover: rgba(colorValue, 0.05),
        color: colorValue,
        border: '1px solid transparent',
      };
    }

    if (input.variant === 'default') {
      return {
        background: 'var(--mantine-color-gray-1)',
        hover: 'var(--mantine-color-gray-2)',
        color: colorValue,
        border: '1px solid var(--mantine-color-gray-3)',
      };
    }
  }

  // For all other colors, use Mantine's default resolver
  return defaultResolvedColors;
};

/**
 * Mantine Theme Configuration
 *
 * Brand colors:
 * - Primary: #4D18C4 (deep purple)
 * - Secondary: #B894F0 (light purple)
 * - Comfortaa font for branding elements only
 *
 * Uses mergeThemeOverrides() for proper deep merge with Mantine 8 defaults.
 * This ensures all default colors (blue, red, orange, etc.) are properly available.
 */
export const mantineTheme = createTheme(
  mergeThemeOverrides(DEFAULT_THEME, {
    /** Primary brand color - deep purple */
    primaryColor: 'brandPrimary',

    /** Color scheme - custom brand colors properly merged with defaults */
    colors: {
      brandPrimary,
      brandSecondary
    },

    /** Variant color resolver - REQUIRED for custom colors to work with variants */
    variantColorResolver,

    /** Font family configuration */
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, "Cascadia Mono", "Segoe UI Mono", "Roboto Mono", "Oxygen Mono", "Ubuntu Monospace", "Source Code Pro", "Fira Mono", "Droid Sans Mono", "Courier New", monospace',

    /** Headings configuration */
    headings: {
      fontFamily: 'Comfortaa, system-ui, -apple-system, sans-serif',
      fontWeight: '600',
      sizes: {
        h1: { fontSize: '2.125rem', lineHeight: '1.2' },
        h2: { fontSize: '1.75rem', lineHeight: '1.25' },
        h3: { fontSize: '1.5rem', lineHeight: '1.3' },
        h4: { fontSize: '1.25rem', lineHeight: '1.35' },
        h5: { fontSize: '1.125rem', lineHeight: '1.4' },
        h6: { fontSize: '1rem', lineHeight: '1.45' }
      }
    },

    /** Spacing scale */
    spacing: {
      xs: '0.5rem',    // 8px
      sm: '0.75rem',   // 12px
      md: '1rem',      // 16px
      lg: '1.5rem',    // 24px
      xl: '2rem'       // 32px
    },

    /** Border radius */
    radius: {
      xs: '0.25rem',   // 4px
      sm: '0.375rem',  // 6px
      md: '0.5rem',    // 8px
      lg: '0.75rem',   // 12px
      xl: '1rem'       // 16px
    },

    /** Shadows */
    shadows: {
      xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
    },

    /** Breakpoints */
    breakpoints: {
      xs: '36em',   // 576px
      sm: '48em',   // 768px
      md: '62em',   // 992px
      lg: '75em',   // 1200px
      xl: '88em'    // 1408px
    },

    /** Default component radius */
    defaultRadius: 'md',

    /** Other settings */
    respectReducedMotion: true,
    cursorType: 'pointer',
    focusRing: 'auto',
    white: '#ffffff',
    black: '#000000',

    /** Component default props */
    components: {
      Button: {
        defaultProps: {
          color: 'brandPrimary'
        }
      },
      ActionIcon: {
        defaultProps: {
          color: 'brandPrimary'
        }
      }
    }
  })
);
